import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import '../../css/admininspector.css';

const AdminInspector = () => {
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ count: 0, results: [] });
  const [services, setServices] = useState({ count: 0, results: [] });
  const [blogs, setBlogs] = useState({ count: 0, results: [] });
  const [categories, setCategories] = useState({ count: 0, results: [] });
  const [tags, setTags] = useState({ count: 0, results: [] });
  const [chatSessions, setChatSessions] = useState({ count: 0, results: [] });
  const [expandedSession, setExpandedSession] = useState(null);

  // Filter states
  const [blogStatus, setBlogStatus] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab !== 'stats') {
      fetchTabData();
    }
  }, [activeTab, searchQuery, blogStatus]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getStats();
      setStats(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch stats. Are you logged in as an admin?');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async () => {
    try {
      setLoading(true);
      setError(null);

      switch (activeTab) {
        case 'users':
          const usersRes = await adminAPI.getUsers({ search: searchQuery });
          setUsers(usersRes.data);
          break;
        case 'services':
          const servicesRes = await adminAPI.getServices({ search: searchQuery });
          setServices(servicesRes.data);
          break;
        case 'blogs':
          const blogsRes = await adminAPI.getBlogs({ search: searchQuery, status: blogStatus });
          setBlogs(blogsRes.data);
          break;
        case 'categories':
          const categoriesRes = await adminAPI.getCategories();
          setCategories(categoriesRes.data);
          break;
        case 'tags':
          const tagsRes = await adminAPI.getTags();
          setTags(tagsRes.data);
          break;
        case 'chat':
          const chatRes = await adminAPI.getChatSessions({ search: searchQuery });
          setChatSessions(chatRes.data);
          break;
        default:
          break;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery('');
    setBlogStatus('');
  };

  const renderStats = () => {
    if (!stats) return null;

    return (
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Users</h3>
          <div className="stat-details">
            <div className="stat-row">
              <span>Total:</span>
              <strong>{stats.users.total}</strong>
            </div>
            <div className="stat-row">
              <span>Active:</span>
              <strong>{stats.users.active}</strong>
            </div>
            <div className="stat-row">
              <span>Staff:</span>
              <strong>{stats.users.staff}</strong>
            </div>
            <div className="stat-row">
              <span>Verified Email:</span>
              <strong>{stats.users.verified_email}</strong>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <h3>Services</h3>
          <div className="stat-details">
            <div className="stat-row">
              <span>Total:</span>
              <strong>{stats.services.total}</strong>
            </div>
            <div className="stat-row">
              <span>Active:</span>
              <strong>{stats.services.active}</strong>
            </div>
            <div className="stat-row">
              <span>Featured:</span>
              <strong>{stats.services.featured}</strong>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <h3>Blog Posts</h3>
          <div className="stat-details">
            <div className="stat-row">
              <span>Total:</span>
              <strong>{stats.blogs.total}</strong>
            </div>
            <div className="stat-row">
              <span>Published:</span>
              <strong>{stats.blogs.published}</strong>
            </div>
            <div className="stat-row">
              <span>Draft:</span>
              <strong>{stats.blogs.draft}</strong>
            </div>
            <div className="stat-row">
              <span>Pending:</span>
              <strong>{stats.blogs.pending}</strong>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <h3>Taxonomy</h3>
          <div className="stat-details">
            <div className="stat-row">
              <span>Categories:</span>
              <strong>{stats.categories}</strong>
            </div>
            <div className="stat-row">
              <span>Tags:</span>
              <strong>{stats.tags}</strong>
            </div>
          </div>
        </div>

        <div className="stat-card">
          <h3>Ubie Chats</h3>
          <div className="stat-details">
            <div className="stat-row">
              <span>Total Sessions:</span>
              <strong>{stats.chat_sessions ?? 0}</strong>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUsers = () => {
    if (users.results.length === 0) {
      return <div className="no-data">No users found</div>;
    }

    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Name</th>
              <th>Company</th>
              <th>Status</th>
              <th>Email Verified</th>
              <th>Last Login</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {users.results.map((user) => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td className="mono">{user.username}</td>
                <td className="mono">{user.email}</td>
                <td>{user.full_name || '-'}</td>
                <td>{user.company_name || '-'}</td>
                <td>
                  <span className={`badge ${user.is_active ? 'badge-success' : 'badge-error'}`}>
                    {user.is_staff ? 'Staff' : user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <span className={`badge ${user.email_verified ? 'badge-success' : 'badge-warning'}`}>
                    {user.email_verified ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="date-cell">{user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</td>
                <td className="date-cell">{new Date(user.date_joined).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderServices = () => {
    if (services.results.length === 0) {
      return <div className="no-data">No services found</div>;
    }

    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Slug</th>
              <th>Categories</th>
              <th>Price</th>
              <th>Featured</th>
              <th>Active</th>
              <th>Popularity</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {services.results.map((service) => (
              <tr key={service.id}>
                <td>{service.id}</td>
                <td>{service.name}</td>
                <td className="mono">{service.slug}</td>
                <td>
                  {service.categories.map((cat, idx) => (
                    <span key={idx} className="mini-badge">{cat}</span>
                  ))}
                </td>
                <td className="price-cell">${service.price} {service.price_unit}</td>
                <td>
                  <span className={`badge ${service.is_featured ? 'badge-success' : 'badge-secondary'}`}>
                    {service.is_featured ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>
                  <span className={`badge ${service.is_active ? 'badge-success' : 'badge-error'}`}>
                    {service.is_active ? 'Yes' : 'No'}
                  </span>
                </td>
                <td>{service.popularity_score}</td>
                <td className="date-cell">{new Date(service.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderBlogs = () => {
    if (blogs.results.length === 0) {
      return <div className="no-data">No blog posts found</div>;
    }

    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Author</th>
              <th>Category</th>
              <th>Tags</th>
              <th>Status</th>
              <th>Views</th>
              <th>Published</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {blogs.results.map((blog) => (
              <tr key={blog.id}>
                <td>{blog.id}</td>
                <td className="truncate">{blog.title}</td>
                <td className="mono">{blog.author_username || 'Unknown'}</td>
                <td>{blog.category_name || '-'}</td>
                <td>
                  {blog.tags?.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="mini-badge">{tag}</span>
                  ))}
                  {blog.tags?.length > 3 && <span className="more-badge">+{blog.tags.length - 3}</span>}
                </td>
                <td>
                  <span className={`badge badge-${blog.status}`}>
                    {blog.status}
                  </span>
                </td>
                <td>{blog.view_count}</td>
                <td className="date-cell">
                  {blog.published_date ? new Date(blog.published_date).toLocaleDateString() : '-'}
                </td>
                <td className="date-cell">{new Date(blog.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCategories = () => {
    if (categories.results.length === 0) {
      return <div className="no-data">No categories found</div>;
    }

    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Slug</th>
              <th>Post Count</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {categories.results.map((category) => (
              <tr key={category.id}>
                <td>{category.id}</td>
                <td>{category.name}</td>
                <td className="mono">{category.slug}</td>
                <td>{category.post_count || 0}</td>
                <td className="date-cell">{new Date(category.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTags = () => {
    if (tags.results.length === 0) {
      return <div className="no-data">No tags found</div>;
    }

    return (
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Slug</th>
              <th>Post Count</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {tags.results.map((tag) => (
              <tr key={tag.id}>
                <td>{tag.id}</td>
                <td>{tag.name}</td>
                <td className="mono">{tag.slug}</td>
                <td>{tag.post_count || 0}</td>
                <td className="date-cell">{new Date(tag.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderChatSessions = () => {
    if (chatSessions.results.length === 0) {
      return <div className="no-data">No chat sessions found</div>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {chatSessions.results.map((s) => (
          <div key={s.id} style={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' }}>
            <div
              onClick={() => setExpandedSession(expandedSession === s.id ? null : s.id)}
              style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <div>
                <span style={{ color: '#a78bfa', fontWeight: 600 }}>
                  {s.user || `anon:${s.session_id?.slice(0, 8)}`}
                </span>
                <span style={{ color: '#666', marginLeft: 12, fontSize: '0.85em' }}>
                  {s.message_count} msgs · {s.total_input_tokens + s.total_output_tokens} tokens
                </span>
                {s.page_url && <span style={{ color: '#555', marginLeft: 12, fontSize: '0.8em' }}>{s.page_url}</span>}
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span style={{ color: '#555', fontSize: '0.8em' }}>
                  {new Date(s.created_at).toLocaleString()}
                </span>
                <span style={{ color: '#a78bfa' }}>{expandedSession === s.id ? '▲' : '▼'}</span>
              </div>
            </div>
            {expandedSession === s.id && (
              <div style={{ borderTop: '1px solid #222', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {s.messages.map((m, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', borderRadius: 6,
                    background: m.role === 'user' ? '#0f1923' : '#1a1a2e',
                    borderLeft: `3px solid ${m.role === 'user' ? '#4A7FA5' : '#a78bfa'}`,
                  }}>
                    <div style={{ color: m.role === 'user' ? '#4A7FA5' : '#a78bfa', fontSize: '0.75em', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {m.role}
                    </div>
                    <div style={{ color: '#ddd', fontSize: '0.9em', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{m.content}</div>
                    {(m.input_tokens > 0 || m.output_tokens > 0) && (
                      <div style={{ color: '#555', fontSize: '0.75em', marginTop: 4 }}>
                        in: {m.input_tokens} · out: {m.output_tokens}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="admin-inspector-page">
      <div className="container">
        <div className="admin-header">
          <h1>Database Inspector</h1>
          <p>Admin-only tool for debugging user accounts, services, blogs, and categories</p>
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        <div className="admin-tabs">
          <button
            className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => handleTabChange('stats')}
          >
            Statistics
          </button>
          <button
            className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => handleTabChange('users')}
          >
            Users ({users.count})
          </button>
          <button
            className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => handleTabChange('services')}
          >
            Services ({services.count})
          </button>
          <button
            className={`tab-btn ${activeTab === 'blogs' ? 'active' : ''}`}
            onClick={() => handleTabChange('blogs')}
          >
            Blogs ({blogs.count})
          </button>
          <button
            className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => handleTabChange('categories')}
          >
            Categories ({categories.count})
          </button>
          <button
            className={`tab-btn ${activeTab === 'tags' ? 'active' : ''}`}
            onClick={() => handleTabChange('tags')}
          >
            Tags ({tags.count})
          </button>
          <button
            className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => handleTabChange('chat')}
          >
            Ubie Chats ({chatSessions.count})
          </button>
        </div>

        {activeTab !== 'stats' && (
          <div className="admin-controls">
            {(activeTab === 'users' || activeTab === 'services' || activeTab === 'blogs' || activeTab === 'chat') && (
              <input
                type="text"
                className="search-input"
                placeholder={activeTab === 'chat' ? 'Search by email, IP, or message content…' : `Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            )}

            {activeTab === 'blogs' && (
              <select
                className="filter-select"
                value={blogStatus}
                onChange={(e) => setBlogStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="published">Published</option>
                <option value="rejected">Rejected</option>
                <option value="archived">Archived</option>
              </select>
            )}
          </div>
        )}

        <div className="admin-content">
          {loading && <div className="loading">Loading...</div>}
          {!loading && activeTab === 'stats' && renderStats()}
          {!loading && activeTab === 'users' && renderUsers()}
          {!loading && activeTab === 'services' && renderServices()}
          {!loading && activeTab === 'blogs' && renderBlogs()}
          {!loading && activeTab === 'categories' && renderCategories()}
          {!loading && activeTab === 'tags' && renderTags()}
          {!loading && activeTab === 'chat' && renderChatSessions()}
        </div>
      </div>
    </div>
  );
};

export default AdminInspector;
